import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDownload, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { SessionService } from '../../core/services/session.service';
import { PortalFilesService } from '../../core/services/portal-files.service';
import { isViewableInBrowser } from '../../core/utils/file-types';

interface PortalFile {
    id: string;
    title: string;
    descr?: string;
    realFilename?: string;
}

interface CategoryGroup {
    categoryId: string;
    categoryName: string;
    files: PortalFile[];
}

@Component({
    selector: 'app-my-files',
    imports: [CommonModule, FontAwesomeModule, NgbTooltip, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './my-files.component.html',
    styleUrl: './my-files.component.scss',
})
export class MyFilesComponent implements OnInit {
    groups: CategoryGroup[] = [];
    loading = false;

    readonly faDownload = faDownload;
    readonly faFolderOpen = faFolderOpen;

    constructor(
        private portalFiles: PortalFilesService,
        private session: SessionService,
        private router: Router,
        private toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        if (!this.session.isLoggedIn()) {
            this.router.navigate(['/']);
            return;
        }
        this.load();
    }

    get hasAnyFiles(): boolean {
        return this.groups.some((g) => g.files.length > 0);
    }

    load(): void {
        this.loading = true;
        this.portalFiles.list().subscribe({
            next: (res) => { this.groups = res?.groups ?? []; this.loading = false; },
            error: () => { this.groups = []; this.loading = false; },
        });
    }

    download(f: PortalFile): void {
        // Decided here, not after the response: a tab opened outside the click
        // handler is treated as a popup and blocked. Only viewable types get a
        // tab, so nothing is left showing a blank page.
        const viewable = isViewableInBrowser(f.realFilename);
        const win = viewable ? window.open('', '_blank') : null;

        this.portalFiles.getDownloadUrl(f.id, viewable).subscribe({
            next: (res) => {
                if (!res?.url) {
                    win?.close();
                    this.toastr.error('Unable to download file');
                    return;
                }
                if (viewable) {
                    // renders in the tab: the URL carries no attachment header
                    if (win) win.location.href = res.url; else window.open(res.url, '_blank');
                    return;
                }
                // saved to disk under the real filename, no tab involved
                const link = document.createElement('a');
                link.href = res.url;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                link.remove();
            },
            error: () => {
                win?.close();
                this.toastr.error('Unable to download file');
            },
        });
    }
}
